import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { JwtAuthService } from '../services/auth/jwt-auth.service';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthStatements } from '../../state/models/auth-statements';
import { OrganizationEntityService } from '../../state/entity-services/organization-entity.service';
import { OrganizationService } from '../services/organization.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { AclService } from '../services/acl.service';
import { DateService } from '../services/date.service';
import { AmplitudeService } from '../services/amplitude.service';
import { ReportsService } from '../services/reports.service';

@Injectable()
export class OrganizationListGuard implements CanActivate {

  queryParams: Record<string, any>;

  constructor(
    private reportsService: ReportsService,
    private dateService: DateService,
    private amplitudeService: AmplitudeService,
    private aclService: AclService,
    private featureFlagService: FeatureFlagService,
    private jwtAuthService: JwtAuthService,
    private organizationService: OrganizationService,
    private organizationEntityService: OrganizationEntityService,
    private router: Router
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.queryParams = route.queryParams;

    if (!this.jwtAuthService.isLoggedIn || !this.jwtAuthService.getUser()?.id) {
      this.redirectToLogin(state.url);
      return of(false);
    }
    return this.aclService.getRoles(this.jwtAuthService.getUser()?.id).pipe(
      switchMap(roles => {
        const { permissions, authStatements } = roles;
        const orgIds = this.getUniqueOrgIds(authStatements);
        return this.organizationEntityService.getWithQuery({ ids: orgIds.join(',') })
          .pipe(
            map((response) => {
              if (this.isAdmin(permissions)) {
                return roles;
              }

              const user = this.jwtAuthService.getUser();
              const orgIds = response.map(org => org.id.toString());
              const orgNames = response.map(org => org.name);
              const orgTypes = response.map(org => org.type);
              const userType = authStatements.map(as => {
                return as.role.name;
              });

              this.amplitudeService.addIdentifyEventArray('user_type', userType);
              this.amplitudeService.addIdentifyEventArray('permission_org_id', orgIds);
              this.amplitudeService.addIdentifyEventArray('permission_org_name', orgNames);
              this.amplitudeService.addIdentifyEventArray('permission_org_type', orgTypes);
              this.amplitudeService.setUserId(user.id.toString());

              const { birthdate, biologicalSex } = user.personProperties;
              this.amplitudeService.addIdentifyEvent('biological_sex', biologicalSex);
              this.amplitudeService.addIdentifyEvent('age', this.dateService.diffByYears(birthdate).toString());

              this.amplitudeService.setIdentify();
              return roles;
            })
          );
      }),
      switchMap(response => {
        const { permissions, authStatements } = response;
        return this.handleAuthStatements(authStatements, permissions);
      })
    );
  }

  private redirectToLogin(returnUrl: string): void {
    this.router.navigate(['/sessions/login'], { queryParams: { return: returnUrl } });
  }

  private handleAuthStatements(authStatements: AuthStatements[], permissions: { name }[]): Observable<boolean> {
    this.organizationService.setOrganization(null);

    if (this.isAdmin(permissions)) {
      this.organizationService.setInfoPermissions({ hasAdmin: true }, authStatements);
      return of(true);
    }

    const orgIds = this.getUniqueOrgIds(authStatements);

    if (orgIds.length === 0) {
      this.jwtAuthService.logout(false);
      this.router.navigate(['/sessions/login']);
    }

    if (orgIds.length > 1) {
      this.organizationService.setInfoPermissions({ hasManyOrgs: true }, authStatements);
      return of(true);
    }
    return this.loadOrganization(orgIds[0], authStatements);
  }

  private isAdmin(permissions: { name } []): boolean {
    return permissions.some(as => as.name === 'ALL_ORGANIZATIONS');
  }

  private getUniqueOrgIds(authStatements: AuthStatements[]): number[] {
    return authStatements
      .filter(as => ['MANAGER', 'DOCTOR'].includes(as.role.name))
      .map(as => as?.resource?.organization?.id)
      .filter((id, index, self) => id && self.indexOf(id) === index);
  }

  private loadOrganization(organizationId: number, authStatements: AuthStatements[]): Observable<boolean> {
    if (!organizationId) {
      return of(false);
    }

    const includeRelatedOrgs: boolean = !!this.queryParams?.includeRelatedOrgs;

    this.organizationService.setInfoPermissions({ hasManyOrgs: false, hasAdmin: false }, authStatements);

    return this.organizationEntityService.getWithQuery({ idsOrRelatedOrgIds: organizationId }).pipe(
      switchMap(organizations => {
          if (organizations.length > 1) {
            this.organizationService.setInfoPermissions({ hasManyOrgs: true }, authStatements);
            if (includeRelatedOrgs) {
              return of(true);
            }
          }
          const orgIds = this.getUniqueOrgIds(authStatements);
          return this.featureFlagService.getFeatureFlagSearchesByOrgId(orgIds[0]).pipe(
            map(() => orgIds[0])
          );
        }
      ),
      switchMap(organizationId => {
        if (typeof organizationId === 'boolean') {
          return of(true);
        }

        return forkJoin([
          this.organizationEntityService.getById(organizationId),
          this.organizationService.$checkHasIncentiveGroup(organizationId),
        ]);
      }),
      map(response => {
        if (typeof response === 'boolean') {
          return true;
        }

        const [organization, reportGroupIncentive] = response;
        this.organizationService.setOrganization(organization);
        this.router.navigateByUrl(this.organizationService.getDefaultUrl());
        return true;
      })
    );
  }
}
